// TES-1664: Fragment markup shifts existing breadcrumb duplicate blocks without changing logic.
// fallow-ignore-file code-duplication
import { Link, useLocation, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { Fragment } from "react";
import { Home } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { breadcrumbKeys } from "@/hooks/queryKeys";
import { breadcrumbForPath } from "@/components/layout/shell/navConfig";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

const MAX_LABEL_LENGTH = 30;

function TruncatedLabel({ label, className }: { label: string; className?: string }) {
  const needsTruncation = label.length > MAX_LABEL_LENGTH;
  const displayLabel = needsTruncation ? `${label.substring(0, MAX_LABEL_LENGTH)}…` : label;

  if (!needsTruncation) {
    return <span className={className}>{label}</span>;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={className}>{displayLabel}</span>
      </TooltipTrigger>
      <TooltipContent>
        <p>{label}</p>
      </TooltipContent>
    </Tooltip>
  );
}

interface BreadcrumbConfig {
  label: string;
  href?: string;
  isLoading?: boolean;
}

// TES-1664: pre-existing breadcrumb branching; this PR only replaces invalid wrapper markup.
// fallow-ignore-next-line complexity
export function AppBreadcrumb() {
  const { t } = useTranslation();
  const location = useLocation();
  const params = useParams();
  const path = location.pathname;

  // Extract IDs from path
  const inspectionIdMatch = path.match(/\/inspections\/([^/]+)/);
  const checklistIdMatch = path.match(/\/checklists\/([^/]+)/);

  const inspectionId = inspectionIdMatch?.[1];
  const checklistId = checklistIdMatch?.[1];

  // Fetch inspection with checklist name
  const { data: inspection, isLoading: isLoadingInspection } = useQuery({
    queryKey: breadcrumbKeys.inspection(inspectionId || ""),
    queryFn: async () => {
      if (!inspectionId) return null;
      const { data, error } = await supabase
        .from("inspections")
        .select(
          `
          id,
          checklists:checklist_id (name),
          inspection_positions:position_id (name)
        `,
        )
        .eq("id", inspectionId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!inspectionId && (path.endsWith("/execute") || path.endsWith("/preview")),
  });

  // Fetch checklist name
  const { data: checklist, isLoading: isLoadingChecklist } = useQuery({
    queryKey: ["breadcrumb-checklist", checklistId],
    queryFn: async () => {
      if (!checklistId || checklistId === "new") return null;
      const { data, error } = await supabase
        .from("checklists")
        .select("id, name")
        .eq("id", checklistId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!checklistId && checklistId !== "new" && path.startsWith("/checklists/"),
  });

  // fallow-ignore-next-line complexity
  const getBreadcrumbs = (): BreadcrumbConfig[] => {
    const breadcrumbs: BreadcrumbConfig[] = [];

    // Statische Routen werden unten via navConfig-SoT (breadcrumbForPath) aufgelöst —
    // kein hartcodiertes routeMap mehr (TES-1683).

    // Handle dynamic routes - Inspection Execute
    if (path.startsWith("/inspections/") && path.endsWith("/execute")) {
      breadcrumbs.push({ label: t("nav.inspections"), href: "/inspections" });

      if (isLoadingInspection) {
        breadcrumbs.push({ label: "", isLoading: true });
      } else if (inspection) {
        const checklistName = (inspection.checklists as { name: string } | null)?.name;
        const positionName = (inspection.inspection_positions as { name: string } | null)?.name;
        const label = positionName
          ? `${checklistName} - ${positionName}`
          : checklistName || t("inspections.execute");
        breadcrumbs.push({ label });
      } else {
        breadcrumbs.push({ label: t("inspections.execute") });
      }
      return breadcrumbs;
    }

    // Handle dynamic routes - Inspection Preview
    if (path.startsWith("/inspections/") && path.endsWith("/preview")) {
      breadcrumbs.push({ label: t("nav.inspections"), href: "/inspections" });

      if (isLoadingInspection) {
        breadcrumbs.push({ label: "", isLoading: true });
      } else if (inspection) {
        const checklistName = (inspection.checklists as { name: string } | null)?.name;
        const positionName = (inspection.inspection_positions as { name: string } | null)?.name;
        const label = positionName
          ? `${checklistName} - ${positionName}`
          : checklistName || t("inspections.preview");
        breadcrumbs.push({ label });
      } else {
        breadcrumbs.push({ label: t("inspections.preview") });
      }
      return breadcrumbs;
    }

    // Handle dynamic routes - Checklist Edit
    if (path.startsWith("/checklists/") && params.id) {
      breadcrumbs.push({ label: t("nav.checklists"), href: "/checklists" });

      if (isLoadingChecklist) {
        breadcrumbs.push({ label: "", isLoading: true });
      } else if (checklist) {
        breadcrumbs.push({ label: checklist.name });
      } else {
        breadcrumbs.push({ label: t("checklists.edit") });
      }
      return breadcrumbs;
    }

    // Handle static routes — Label + Parent-Sektion aus navConfig abgeleitet (SoT, TES-1683):
    // Breadcrumb-Parent == Sidebar-Sektion, kein driftendes Zweit-Mapping mehr.
    const route = breadcrumbForPath(path);
    if (route) {
      if (route.parentLabelKey) {
        breadcrumbs.push({ label: t(route.parentLabelKey) });
      }
      breadcrumbs.push({ label: t(route.labelKey) });
    }

    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <Breadcrumb data-testid="app-breadcrumb">
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/" className="flex items-center gap-1.5">
              <Home className="h-4 w-4" />
              <span className="sr-only">{t("nav.dashboard")}</span>
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>

        {breadcrumbs.map((crumb, index) => (
          <Fragment key={index}>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              {crumb.isLoading ? (
                // TES-1660: Skeleton-Höhe = finale Text-Zeilenbox. BreadcrumbList ist
                // `text-sm` (line-height 1.25rem = 20px = h-5); ein h-4-Skeleton (16px)
                // ließ die TopAppBar beim Auflösen der async Crumb-Daten springen. Der
                // min-h-5-Wrapper reserviert die volle Zeilenbox unabhängig vom Skeleton.
                <span
                  className="inline-flex min-h-5 items-center"
                  data-testid="breadcrumb-loading-label"
                >
                  <Skeleton className="h-5 w-24" />
                </span>
              ) : index === breadcrumbs.length - 1 || !crumb.href ? (
                <BreadcrumbPage className="truncate">
                  <TruncatedLabel label={crumb.label} />
                </BreadcrumbPage>
              ) : (
                <BreadcrumbLink asChild>
                  <Link to={crumb.href}>
                    <TruncatedLabel label={crumb.label} />
                  </Link>
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
          </Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
